import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  LineChart, Line, Legend,
} from "recharts";
import api from "@/lib/api";
import { inr, inr0, fmtDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, KPI, Section, Empty } from "@/components/Bits";
import StatusChip from "@/components/StatusChip";

export default function Dashboard() {
  const { isOwner, user } = useAuth();
  const [d, setD] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/dashboard").then((r) => setD(r.data));
  }, []);

  if (!d) return <div className="text-[#8C929B]">Loading dashboard…</div>;

  const aging = [
    { k: "0–30 days", v: d.aging["0-30"] },
    { k: "31–60 days", v: d.aging["31-60"] },
    { k: "61–90 days", v: d.aging["61-90"] },
    { k: "90+ days", v: d.aging["90+"] },
  ];

  return (
    <div>
      <PageHeader
        eyebrow={`Good day, ${user?.name?.split(" ")[0]}`}
        title="Dashboard"
        subtitle="A quiet overview of the shop today — sales, stock and money owed, reconciled to the ledger."
      />

      {/* KPI grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <KPI testid="kpi-today-sales" label="Today's Sales" value={inr(d.today_sales)} sub={`${d.today_invoice_count} invoices today`} accent="ink" />
        <KPI testid="kpi-mtd-revenue" label="Sales This Month" value={inr(d.mtd_revenue)} sub={`${inr0(d.mtd_net_sales)} excl. GST`} accent="ink" />
        {isOwner && <KPI testid="kpi-mtd-profit" label="Gross Profit (MTD)" value={inr(d.mtd_profit)} sub={`Net sales − COGS ${inr0(d.mtd_cogs)}`} accent="sage" />}
        {isOwner && <KPI testid="kpi-mtd-net-profit" label="Net Profit (MTD)" value={inr(d.mtd_net_profit)} sub={`After ${inr0(d.mtd_expenses)} expenses`} accent={d.mtd_net_profit < 0 ? "terra" : "sage"} />}
        <KPI testid="kpi-receivables" label="Receivables" value={inr(d.receivables)} sub="Customers owe you" accent="terra" />
        {isOwner && <KPI testid="kpi-payables" label="Payables" value={inr(d.payables)} sub="You owe vendors" accent="terra" />}
        {isOwner && <KPI testid="kpi-inv-cost" label="Inventory at Cost" value={inr0(d.inventory_value_cost)} sub={`${inr0(d.inventory_value_retail)} at retail`} accent="gold" />}
        {!isOwner && <KPI testid="kpi-inv-retail" label="Inventory at Retail" value={inr0(d.inventory_value_retail)} sub="Ticket value" accent="ink" />}
        <KPI testid="kpi-low-stock" label="Low / Out of Stock" value={`${d.low_stock_count} / ${d.out_of_stock_count}`} sub="Variants to reorder" accent="gold" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mb-4">
        <Section title={isOwner ? "Sales, Purchases & Expenses" : "Sales vs Purchases"} className={isOwner ? "lg:col-span-2" : "lg:col-span-3"}>
          <div className="p-5 h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={d.chart} barGap={4}>
                <CartesianGrid vertical={false} stroke="#E6E1D8" />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#8C929B" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#8C929B" }} axisLine={false} tickLine={false} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => inr(v)} contentStyle={{ borderRadius: 10, border: "1px solid #E6E1D8", fontSize: 13 }} />
                <Bar dataKey="sales" fill="#0E1B2E" radius={[4, 4, 0, 0]} name="Net sales" />
                <Bar dataKey="purchases" fill="#C5A059" radius={[4, 4, 0, 0]} name="Purchases received" />
                {isOwner && <Bar dataKey="expenses" fill="#A3411D" radius={[4, 4, 0, 0]} name="Expenses" />}
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12 }} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Section>

        {isOwner && (
          <Section title="Net Profit Trend">
            <div className="p-5 h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={d.chart}>
                  <CartesianGrid vertical={false} stroke="#E6E1D8" />
                  <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#8C929B" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#8C929B" }} axisLine={false} tickLine={false} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v) => inr(v)} contentStyle={{ borderRadius: 10, border: "1px solid #E6E1D8", fontSize: 13 }} />
                  <Line type="monotone" dataKey="profit" stroke="#2D5A3C" strokeWidth={2.5} dot={{ r: 3 }} name="Net profit" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Section>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mb-4">
        <Section title="Recent Invoices" className="lg:col-span-2">
          <table className="tbl">
            <thead>
              <tr><th>Invoice</th><th>Customer</th><th>Channel</th><th>Date</th><th className="num">Amount</th><th>Status</th></tr>
            </thead>
            <tbody>
              {d.recent_invoices.map((i) => (
                <tr key={i.id} className="cursor-pointer" onClick={() => nav(`/invoices/${i.id}`)} data-testid={`recent-inv-${i.invoice_number}`}>
                  <td className="font-num">{i.invoice_number}</td>
                  <td>{i.customer_name}</td>
                  <td className="text-[#5C626A]">{i.channel}</td>
                  <td className="text-[#5C626A]">{fmtDate(i.date)}</td>
                  <td className="num font-num">{inr(i.total)}</td>
                  <td><StatusChip status={i.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>

        <Section title="Receivables Aging">
          <div className="p-5 space-y-3">
            {aging.map((a) => (
              <div key={a.k} className="flex items-center justify-between">
                <span className="text-sm text-[#5C626A]">{a.k}</span>
                <span className="font-num text-sm" style={{ color: a.k.includes("90+") ? "#A3411D" : "#1A1D20" }}>{inr(a.v)}</span>
              </div>
            ))}
            <div className="border-t border-[#E6E1D8] pt-3 flex items-center justify-between">
              <span className="text-sm font-semibold">Total Due</span>
              <span className="font-num font-semibold">{inr(d.receivables)}</span>
            </div>
          </div>
        </Section>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Section title="Top Styles (MTD)">
          {d.top_styles.length === 0 ? <Empty>No sales yet this month</Empty> : (
            <div className="p-5 space-y-3">
              {d.top_styles.map((s, idx) => (
                <div key={s.name} className="flex items-center gap-3">
                  <span className="font-num text-xs text-[#8C929B] w-5">{idx + 1}</span>
                  <span className="text-sm flex-1 truncate">{s.name}</span>
                  <span className="font-num text-sm">{inr0(s.revenue)}</span>
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section title="Reorder List" action={<button className="btn btn-ghost btn-sm" onClick={() => nav("/inventory")}>View inventory</button>}>
          {d.reorder_list.length === 0 ? <Empty>Everything well stocked</Empty> : (
            <table className="tbl">
              <thead><tr><th>Style</th><th>Variant</th><th className="num">On hand</th><th></th></tr></thead>
              <tbody>
                {d.reorder_list.map((r, i) => (
                  <tr key={i}>
                    <td>{r.name}</td>
                    <td className="text-[#5C626A]">{r.color} / {r.size}</td>
                    <td className="num font-num">{r.current_qty}</td>
                    <td><StatusChip status={r.current_qty === 0 ? "Out of stock" : "Low stock"} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Section>
      </div>
    </div>
  );
}
